import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Table } from 'primeng/table';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { LoaderService } from '../services/loader.service';
import { PreviousRouteService } from '../services/previous-route.service';
import { ResultsService } from '../services/results.service';
import { AppConstants } from '../shared/app.constants';
import { AppPermissions } from '../shared/app.permissions';
import { AppUtils } from '../shared/app.utils';
import { AuthorizationByStatusModel } from '../shared/models/authorization-model';
import { ResultFilterModel } from '../shared/models/result-filter.model';

@Component({
  selector: 'ness-results',
  templateUrl: './results.component.html'
})
export class ResultsComponent implements OnDestroy, OnInit {
  @ViewChild(Table, { static: false }) dataTable: Table;
  cols: any[];
  filterModel: ResultFilterModel;
  loading: boolean;
  loadingStartRow: number = 1;
  loadingEndRow: number;
  messageTemplate: string;
  openSidebar: boolean = false;
  openViewDetailForm: boolean = false;
  resultsData: ServerModels.ResultTableViewModel[] = [];
  rows: number = AppConstants.defaultTableRows;
  selectedResult: ServerModels.ResultTableViewModel[];
  selectedResultId$: BehaviorSubject<string> = new BehaviorSubject(null);
  readonly storageKey: string = "resultsFilterModel";

  private _unsubscribe$: Subject<void> = new Subject<void>();
  private _allFilters: ServerModels.ENESS_REF_SuccessStoryFilters[] = [];

  get canCreate(): AuthorizationByStatusModel {
    return AppPermissions.canCreateResult;
  }

  constructor(
    private resultService: ResultsService,
    private loaderService: LoaderService,
    public hostElement: ElementRef,
    private previousRouteService: PreviousRouteService,
    private router: Router
  ) { }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  ngOnInit() {
    this.cols = [
      { field: 'Status', header: 'Status' },
      { field: 'TrackingNumber', header: 'Results Tracking #' },
      { field: 'CreatedBy', header: 'Created By' },
      { field: 'CreatedDate', header: 'Created Date' },
      { field: 'Defendants', header: 'Defendant(s)' },
      { field: 'RecoveredFirearms', header: 'Recovered Firearms' },
      { field: 'LastUpdateDate', header: 'Last Updated Date' }
    ];

    this.resultService.getFilters().pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (filter: ServerModels.ENESS_REF_SuccessStoryFilters[]) => {
        this._allFilters = filter;
      },
      complete: () => {
        let fm: any;
        if (fm = JSON.parse(sessionStorage.getItem(this.storageKey))) {
          this.filterModel = new ResultFilterModel(fm._cgicCode, fm._currentView);
        } else {
          let currentView = this._allFilters.length > 0 ? this._allFilters[0].SuccessStoryFilterID : "";
          this.filterModel = new ResultFilterModel(AppUtils.userCGICCode(), currentView);
          sessionStorage.setItem(this.storageKey, JSON.stringify(this.filterModel));
        }

        this.getCurrentView(0, this.filterModel);
      }
    });
  }

  applyFilters(filters: ResultFilterModel) {
    this.resultsData = [];
    this.messageTemplate = 'Loading data...';
    this.dataTable.reset();
    this.getCurrentView(0, filters);
  }

  lookupResultFilterId(name: string): string {
    return this._allFilters.find(item => { return item.SuccessStoryFilter.toString().toLowerCase() == name.toLowerCase() }).SuccessStoryFilterID;
  }

  viewDetails(selectedItem: ServerModels.ResultTableViewModel) {
    this.selectedResultId$.next(selectedItem.SuccessStoryID);
    this.openSidebar = true;
    this.openViewDetailForm = true;    
  }

  private getCurrentView(pageNumber: number, filters: ResultFilterModel) {
    this.resultService.getResults(pageNumber, filters).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (data: ServerModels.ResultTableViewModel[]) => {
        this.loading = data.length > 0;
        if (data.length > 0) {
          this.resultsData = this.resultsData.concat(data);
          let nextPage = pageNumber + 1;
          this.loadingStartRow = pageNumber == 0 ? 1 : (data.length + this.resultsData.length);
          this.loadingEndRow = this.resultsData.length;
          this.messageTemplate = `Loaded rows ${this.loadingStartRow} thru ${this.loadingEndRow}...`;
          if (this.loading = data.length == AppConstants.dbPagingDefaultRows) {
            this.getCurrentView(nextPage, filters);
          }
        }
      }
    });
  }

  public onShowSidebar(): void {
    window.setTimeout(() => {
      let targetEl = this.hostElement.nativeElement.getElementsByClassName('ui-sidebar-active');
      if (targetEl.length) {
        targetEl[0].scrollTop = 0;
        targetEl[0].setAttribute("tabindex", "-1");
        targetEl[0].focus();
      }
    }, 1000);
  }

  public newResult(): void {
    this.previousRouteService.returnUrl = this.router.url;
    this.router.navigate(['/result-form']);
  }
}
